import { useCallback, useEffect, useMemo } from 'react';

import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { firstOf, secondOf } from 'utils/arrays/elements.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromEntriesToObject from 'utils/converters/object/fromEntriesToObject.util';
import fromObjectToEntries from 'utils/converters/object/fromObjectToEntries.util';
import logger from 'utils/logger.util';
import transformObject from 'utils/objects/transformObject.util';
import isNotNilValue from 'utils/predicates/any/isNotNilValue.util';
import isObjectShallowEqual from 'utils/predicates/object/isObjectShallowEqual.util';

const L = logger('useCheckmap'); // eslint-disable-line no-unused-vars

const prepareInit = (value) => {
  if (Array.isArray(value)) {
    return fromEntriesToObject(value.map((key) => [key, false]));
  }

  return fromEntriesToObject(
    fromObjectToEntries(value).map(([key, val]) => [fromAnyToTrimmed(key), fromAnyToBool(val)]),
  );
};

export const useCheckmap = (init) => {
  const [localVal, setLocalVal] = useStateWhileMounted(null);

  const set = useCallback(
    (next) => {
      // to avoid loops and
      // generally because there is no need to update the state if the values are equal
      // just stop here
      if (isObjectShallowEqual(localVal, next)) return;

      setLocalVal(next);
    },
    [setLocalVal, localVal],
  );

  useEffect(() => {
    // only initialize once, at the start, while the localVal is null
    if (isNotNilValue(localVal)) return;

    // initializing with this useEffect hook,
    // instead of the straightforward pass of the init argument to useResettingState
    // it is possible to check if the values are equal through the set function
    return void set(init && prepareInit(init));
  }, [init, localVal, set]);

  const tip = useCallback(
    (key) => {
      const k = fromAnyToTrimmed(key);
      const v = localVal ?? {};

      const next = { ...v, [k]: !v[k] };

      set(next);
    },
    [localVal, set],
  );

  const only = useCallback(
    (k, v) => {
      const inv = !v;

      const transformer = (entries) => entries.map(([key]) => [key, inv]);
      const next = transformObject(transformer, localVal ?? {});

      next[k] = fromAnyToBool(v);

      set(next);
    },
    [localVal, set],
  );

  const every = useCallback(
    (v) => {
      v = fromAnyToBool(v);

      const transformer = (entries) => entries.map(([key]) => [key, v]);
      const next = transformObject(transformer, localVal ?? {});

      set(next);
    },
    [localVal, set],
  );

  return useMemo(() => {
    const val = localVal ?? {};

    const all = fromObjectToEntries(val);

    const truthy = all.filter(secondOf);
    const falsey = all.filter((entry) => !secondOf(entry));

    const yay = (key) => set((prev) => ({ ...prev, [fromAnyToTrimmed(key)]: true }));
    const nay = (key) => set((prev) => ({ ...prev, [fromAnyToTrimmed(key)]: false }));

    const onlyTrue = (key) => void only(key, true);
    const onlyFalse = (key) => void only(key, false);

    const everyTrue = () => void every(true);
    const everyFalse = () => void every(false);

    const aKeys = all.map(firstOf);
    const tKeys = truthy.map(firstOf);
    const fKeys = falsey.map(firstOf);

    return {
      val,
      set,

      tip,
      yay,
      nay,

      only,
      onlyTrue,
      onlyFalse,

      every,
      everyTrue,
      everyFalse,

      all,
      truthy,
      falsey,

      aKeys,
      tKeys,
      fKeys,
    };
  }, [every, localVal, only, set, tip]);
};
